import { Box } from "@mui/material";
import React from "react";
import useTheme from "../../../../../../../common/hooks/useTheme";
import { Theme } from "../../../../../../../providers/ThemeProvider";
import InfoIcon from "../../../../../svg/InfoIcon";
import CustomTooltip from "../../../../common/CustomTooltip";
import { getScoreLevel } from "../../helpers/mealItemHelpers";
import PlanTag from "../shared/PlanTag";

export type MetadataColumn = {
  header: string;
  value: string;
};

type Props = {
  /** Serving size (food) or Preparation (recipe). */
  leading: MetadataColumn;
  score: number | null;
  /** Difficulty (recipes only). */
  trailing?: MetadataColumn;
};

const SCORE_INFO =
  "The meal score rates how well this item fits your plan's targets: Low 0–35, Moderate 36–59, High 60 and above.";

/** The two- or three-column strip under the title: serving/prep, score, difficulty. */
const DetailsMetadataRow = ({ leading, score, trailing }: Props) => {
  const theme: Theme = useTheme();

  const Column = ({
    header,
    children,
    divider,
  }: {
    header: React.ReactNode;
    children: React.ReactNode;
    divider: boolean;
  }) => (
    <Box sx={styles.column(theme, divider)}>
      <Box sx={styles.header(theme)}>{header}</Box>
      <Box sx={styles.value(theme)}>{children}</Box>
    </Box>
  );

  return (
    <Box sx={styles.row(theme)}>
      <Column header={leading.header} divider={true}>
        {leading.value}
      </Column>
      <Column
        header={
          <Box sx={styles.scoreHeader}>
            Meal score
            <CustomTooltip message={SCORE_INFO} type="medium">
              <Box sx={styles.info}>
                <InfoIcon
                  size={12}
                  color={theme.colors_ds.NEUTRAL_ICON_DEFAULT}
                />
              </Box>
            </CustomTooltip>
          </Box>
        }
        divider={!!trailing}
      >
        {score === null ? (
          "Not available"
        ) : (
          <PlanTag kind="score" level={getScoreLevel(score)} score={score} />
        )}
      </Column>
      {trailing && (
        <Column header={trailing.header} divider={false}>
          {trailing.value}
        </Column>
      )}
    </Box>
  );
};

export default DetailsMetadataRow;

const styles = {
  row: (theme: Theme) => ({
    display: "flex",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "12px",
    overflow: "hidden",
  }),
  column: (theme: Theme, divider: boolean) => ({
    flex: 1,
    display: "flex",
    flexDirection: "column" as "column",
    gap: "6px",
    padding: "12px 16px",
    borderRight: divider
      ? `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`
      : "none",
  }),
  header: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "16px",
  }),
  scoreHeader: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  info: {
    display: "flex",
    cursor: "help",
  },
  value: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
};
